import { useState, useMemo, useEffect } from "react";
// @mui
import { Grid } from "@mui/material";
// components
import BottomScrollListener from "~src/components/BottomScrollListener";
import { SkeletonCard } from "~src/components/SkeletonCard";
import { DialogAnimate } from "~src/components/animate";
import { TrendingAdTile } from "./TrendingAdTile";
import AdDetailPreviewModal from "../AdDetail";
// hooks
import useCollapseDrawer from "~src/hooks/useCollapseDrawer";
import useResponsive from "~src/hooks/useResponsive";
import { TrendingAdsContext } from "./useInfiniteTrendingAds";
// types
import { TrendingAd } from "~src/types/ad";
import { AdBlockerNotification } from "~src/components/AdBlockerNotification";
import EmptyContent from "~src/components/EmptyContent";
import ContentBox from "~src/components/ContentBox";
// utils
import { isValidImage, isValidVideo } from "./adValidation";
const popupStyle: React.CSSProperties = {
  width: "100%",
  margin: "0 auto",
  wordWrap: "break-word", // Allow long words to break and wrap within the container
  overflowWrap: "break-word", // Allow words to wrap within the container even if there are no explicit spaces or hyphens
};

// ---------------------------------------------------------------------------------------------------------------------------

export const TrendingAdsList = ({
  trendingAdsContext,
}: {
  trendingAdsContext: TrendingAdsContext;
}) => {
  const { isCollapse } = useCollapseDrawer();
  const isDesktop = useResponsive("up", "md");
  const [selectedAd, setSelectedAd] = useState<TrendingAd>();

  const { data, loading, loadMore, setValidCount } = trendingAdsContext;

  const viewNextAd = () => {
    if (data) {
      const index = data.findIndex((ad) => ad.id === selectedAd?.id);
      if (index === data.length) {
        return;
      }
      setSelectedAd(data[index + 1] as unknown as TrendingAd);
    }
  };

  const viewPreviousAd = () => {
    if (data) {
      const index = data.findIndex((a) => a.id === selectedAd?.id);
      if (index < 1) {
        return;
      }
      setSelectedAd(data[index - 1] as unknown as TrendingAd);
    }
  };

  // Function to filter valid ads for display - memoized for performance
  const validAds = useMemo(() => {
    const validAds: TrendingAd[] = [];

    for (const ad of data) {
      // Only add ads with valid media
      if (
        ((!ad.ad_video || ad.ad_video === "N/A") && isValidImage(ad.ad_image)) ||
        (ad.ad_video && ad.ad_video !== "N/A" && isValidVideo(ad.ad_video))
      ) {
        validAds.push(ad);
      }
    }

    return validAds;
  }, [data]);

  // Update the valid count in context after render to avoid setState during render
  useEffect(() => {
    setValidCount(validAds.length);
  }, [validAds, setValidCount]);

  if (trendingAdsContext.total == 0 && loading === false) {
    return (
      <ContentBox sx={{ mt: 3 }} data-testid="trending-ad-tile-section">
        <EmptyContent
          title="No Ads found"
          description="Try clearing your filters or using another search term."
        />
      </ContentBox>
    );
  }

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          mt: 2,
        }}
        data-testid="trending-ad-tile-section"
      >
        {validAds.map((ad, index) => (
          <Grid 
            key={`ad-${ad.id}-${index}`}
            size={{ xs: 12, md: isCollapse ? 3 : 4 , sm: 6 }}
            data-testid="trending-ad-tile-grid"
          >
            <TrendingAdTile
              ad={ad}
              onClick={() => {
                setSelectedAd(ad as unknown as TrendingAd);
              }}
            />
          </Grid>
        ))}
        {loading &&
          Array(4)
            .fill(null)
            .map((_, index) => (
              <Grid key={`skeleton-${index}`} size={{ xs: 12, md: isCollapse ? 3 : 4 , sm: 6 }}  >
                <SkeletonCard />
              </Grid>
            ))}
        <BottomScrollListener
          onBottom={() => {
            loadMore();
          }}
          offset={500}
        />
      </Grid>
      <DialogAnimate
        PaperProps={{ style: popupStyle }}
        open={selectedAd !== undefined}
        onClose={() => {
          setSelectedAd(undefined);
        }}
        maxWidth={isDesktop ? "md" : "xs"}
      >
        <AdDetailPreviewModal
          ad={selectedAd}
          handleClose={() => setSelectedAd(undefined)}
          onNext={viewNextAd}
          onPrevious={viewPreviousAd}
        />
      </DialogAnimate>
      {loading && (
        <AdBlockerNotification initialLoad={trendingAdsContext.initialLoad} />
      )}
    </>
  );
};
